import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import { RootState } from "../store";
import { PermissionEnum } from "../Components/constants/permissions";

/**
 * Hook to get user permissions as an array of PermissionEnum values
 * Checks sessionStorage (authUser) for permissions array
 */
export const usePermissions = (): PermissionEnum[] => {
  // Get permissions from sessionStorage (primary source)
  const getPermissionsFromStorage = (): PermissionEnum[] => {
    try {
      const authUser = sessionStorage.getItem("authUser");
      if (authUser) {
        const userData = JSON.parse(authUser);
        // If permissions are stored directly as PermissionEnum array
        if (userData.permissions && Array.isArray(userData.permissions)) {
          const validPermissions = userData.permissions.filter((p: string) =>
            Object.values(PermissionEnum).includes(p as PermissionEnum)
          ) as PermissionEnum[];
          
          // If user has ALL permission, return all permissions
          if (validPermissions.includes(PermissionEnum.ALL)) {
            return Object.values(PermissionEnum);
          }
          return validPermissions;
        }
      }
    } catch (error) {
      console.error("Error parsing permissions from sessionStorage:", error);
    }
    return [];
  };

  // Get permissions from Redux login state
  const selectLoginState = (state: RootState) => (state as any).Login;
  const loginSelector = createSelector(selectLoginState, (login) => ({
    permissions: login.permissions || [],
    useraccessrights: login.useraccessrights || {},
  }));

  const { permissions: reduxPermissions, useraccessrights } = useSelector(loginSelector);

  // Extract permissions from Redux permissions array (from API)
  const getPermissionsFromRedux = (): PermissionEnum[] => {
    if (reduxPermissions && Array.isArray(reduxPermissions) && reduxPermissions.length > 0) {
      const permissionNames = reduxPermissions.map((p: any) => p.permissionname || p);
      const validPermissions = permissionNames.filter((p: string) =>
        Object.values(PermissionEnum).includes(p as PermissionEnum)
      ) as PermissionEnum[];
      
      // If user has ALL permission, return all permissions
      if (validPermissions.includes(PermissionEnum.ALL)) {
        return Object.values(PermissionEnum);
      }
      return validPermissions;
    }
    return [];
  };

  // Extract permissions from useraccessrights (legacy fallback)
  const getPermissionsFromAccessRights = (): PermissionEnum[] => {
    if (!useraccessrights || !useraccessrights.permissions) return [];
    
    const permissions = useraccessrights.permissions;
    if (Array.isArray(permissions)) {
      const validPermissions = permissions.filter((p: string) =>
        Object.values(PermissionEnum).includes(p as PermissionEnum)
      ) as PermissionEnum[];
      
      if (validPermissions.includes(PermissionEnum.ALL)) {
        return Object.values(PermissionEnum);
      }
      return validPermissions;
    }
    return [];
  };

  // Priority: sessionStorage > Redux permissions > useraccessrights
  const storagePermissions = getPermissionsFromStorage();
  if (storagePermissions.length > 0) {
    return storagePermissions;
  }

  const reduxPermissionsList = getPermissionsFromRedux();
  if (reduxPermissionsList.length > 0) {
    return reduxPermissionsList;
  }

  return getPermissionsFromAccessRights();
};
